import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, X, Eye, EyeOff, Check, FileText } from 'lucide-react';
import { centralizedErrorHandler, type ErrorLog } from '@/utils/errorHandling/centralizedErrorHandler';
import { useToast } from '@/hooks/use-toast';

interface ErrorNotificationProps {
  maxVisible?: number;
  autoHideDuration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const ErrorNotificationSystem: React.FC<ErrorNotificationProps> = ({
  maxVisible = 3,
  autoHideDuration = 5000,
  position = 'top-right'
}) => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { toast } = useToast();

  // Подписка на новые ошибки
  useEffect(() => {
    const interval = setInterval(() => {
      const errorLogs = centralizedErrorHandler.getErrorLogs();
      const newErrors = errorLogs.slice(0, maxVisible);
      setErrors(newErrors);
    }, 1000);

    return () => clearInterval(interval);
  }, [maxVisible]);

  // Автоматическое скрытие уведомлений
  useEffect(() => {
    if (errors.length > 0 && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        setErrors([]);
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [errors, autoHideDuration]);

  const handleDismiss = (index: number) => {
    setErrors(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setErrors([]);
    centralizedErrorHandler.clearErrorLogs();
  };

  const handleCopyError = async (error: ErrorLog, errorId: string) => {
    const errorText = `
Ошибка: ${error.error?.message || 'Неизвестная ошибка'}
Время: ${new Date(error.timestamp).toLocaleString()}
Компонент: ${error.context?.component || 'Неизвестно'}
Операция: ${error.context?.operation || 'Неизвестно'}
URL: ${error.url}
Стек: ${error.error?.stack || 'Недоступен'}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      setCopiedId(errorId);
      toast({
        title: 'Скопировано',
        description: 'Информация об ошибке скопирована в буфер обмена',
      });
      
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast({
        title: 'Ошибка',
        description: 'Не удалось скопировать в буфер обмена',
        variant: 'destructive',
      });
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
      default:
        return 'top-4 right-4';
    }
  };

  if (errors.length === 0) return null;

  return (
    <div className={`fixed ${getPositionClasses()} z-50 w-96 max-w-[90vw] space-y-2`}>
      {/* Заголовок с управлением */}
      <Card className="border-destructive bg-destructive/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span>Ошибки ({errors.length})</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearAll}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Список ошибок */}
      {!collapsed && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {errors.map((error, index) => {
            const errorId = `error-${index}-${error.timestamp}`;
            const isNetworkError = error.error?.message?.includes('fetch') || 
                                  error.error?.message?.includes('network');
            
            return (
              <Alert key={errorId} variant="destructive" className="relative">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="pr-8">
                  <div className="space-y-2">
                    <div className="font-medium text-sm">
                      {isNetworkError ? 'Ошибка сети' : 'Системная ошибка'}
                    </div>
                    
                    <div className="text-xs text-muted-foreground">
                      {error.error?.message || 'Неизвестная ошибка'}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {new Date(error.timestamp).toLocaleTimeString()}
                      </span>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => handleCopyError(error, errorId)}
                        >
                          {copiedId === errorId ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <FileText className="h-3 w-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => handleDismiss(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {error.context?.component && (
                      <div className="text-xs text-muted-foreground">
                        Компонент: {error.context.component}
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Хук для управления уведомлениями об ошибках
export const useErrorNotifications = () => {
  const [enabled, setEnabled] = useState(true);
  const [position, setPosition] = useState<'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'>('top-right');
  const [maxVisible, setMaxVisible] = useState(3);

  const toggleNotifications = () => setEnabled(prev => !prev);
  
  const changePosition = (newPosition: typeof position) => setPosition(newPosition);
  
  const changeMaxVisible = (count: number) => setMaxVisible(Math.max(1, Math.min(10, count)));

  return {
    enabled,
    position,
    maxVisible,
    toggleNotifications,
    changePosition,
    changeMaxVisible
  };
};
